import * as XLSX from 'xlsx';

export type ImportSourceFormat = 'csv' | 'json' | 'html';
export type ImportTemplateFormat = 'csv' | 'json' | 'xlsx';

type FactorDefinition = {
  kind: 'gain' | 'pain';
  label: string;
  weight: number;
  examples: [number, number];
};

const BASE_COLUMNS = [
  'opportunity_id',
  'opportunity_name',
  'theme',
  'Rank',
  'Gain_Index',
  'Pain_Index',
  'Priority_Score',
  'status',
  'source',
  'wave',
  'decision_label',
  'quadrant',
  'confidence_score',
  'type_internal_external',
  'impact_type',
  'problem_statement',
  'solution_summary',
  'potential_impact',
  'feasibility_notes',
  'evidence_summary',
  'evidence_caveat',
  'source_systems',
  'data_fields_needed',
  'pilot_wedge',
  'opportunity_owner',
  'strategic_tags',
] as const;

const GAIN_FACTORS: FactorDefinition[] = [
  { kind: 'gain', label: 'Revenue Expansion', weight: 0.3, examples: [5, 5] },
  { kind: 'gain', label: 'Strategic Differentiation / AI Positioning', weight: 0.25, examples: [5, 4] },
  { kind: 'gain', label: 'Margin Protection / Delivery Leverage', weight: 0.2, examples: [4, 3] },
  { kind: 'gain', label: 'Reusability / Productization Leverage', weight: 0.15, examples: [4, 5] },
  { kind: 'gain', label: 'Client / Market Adoption Potential', weight: 0.1, examples: [4, 4] },
];

const PAIN_FACTORS: FactorDefinition[] = [
  { kind: 'pain', label: 'Data & Knowledge Readiness', weight: 0.2, examples: [2, 3] },
  { kind: 'pain', label: 'Technical / Integration Complexity', weight: 0.15, examples: [2, 2] },
  { kind: 'pain', label: 'Workflow & Change Complexity', weight: 0.15, examples: [3, 2] },
  { kind: 'pain', label: 'Adoption & Behavior-Change Risk', weight: 0.1, examples: [2, 2] },
  { kind: 'pain', label: 'Organizational Capacity / Execution Readiness', weight: 0.1, examples: [2, 2] },
  { kind: 'pain', label: 'Governance / Compliance Burden', weight: 0.1, examples: [2, 2] },
  { kind: 'pain', label: 'Source-of-Truth Risk', weight: 0.1, examples: [3, 3] },
  { kind: 'pain', label: 'Testability / Measurement', weight: 0.1, examples: [2, 2] },
];

const factorColumns = (kind: 'gain' | 'pain', count: number) =>
  Array.from({ length: count }, (_, index) => [
    `${kind}_factor_${index + 1}_label`,
    `${kind}_factor_${index + 1}_weight`,
  ]).flat();

const scoreColumns = (kind: 'gain' | 'pain', count: number) =>
  Array.from({ length: count }, (_, index) => `${kind}_${index + 1}`);

export const IMPORT_COLUMNS = [
  ...BASE_COLUMNS,
  ...factorColumns('gain', GAIN_FACTORS.length),
  ...factorColumns('pain', PAIN_FACTORS.length),
  ...scoreColumns('gain', GAIN_FACTORS.length),
  ...scoreColumns('pain', PAIN_FACTORS.length),
] as const;

const TEMPLATE_ROWS = [
  withFactorColumns(
    {
      opportunity_id: 'RN-1',
      opportunity_name: 'Agentic Prospecting + BD Intelligence',
      theme: 'Business Development',
      Rank: '1',
      Gain_Index: '86',
      Pain_Index: '41',
      Priority_Score: '77.5',
      status: 'active',
      source: 'Eliza stakeholder interviews',
      wave: 'Wave 1',
      decision_label: 'Strategic bet',
      quadrant: 'Strategic Bets',
      confidence_score: '3',
      type_internal_external: 'Internal',
      impact_type: 'Revenue',
      problem_statement: 'No systematic targeting engine',
      solution_summary: 'Use public signals before CRM data is fixed',
      potential_impact: 'Revenue expansion and stronger account targeting',
      feasibility_notes: 'Salesforce quality remains a caveat',
      evidence_summary: 'COO and BD confirmed fragmented lists',
      evidence_caveat: 'Project Orbit dependency',
      source_systems: 'Salesforce; Project Orbit',
      data_fields_needed: 'account lists; public signals; CRM owner',
      pilot_wedge: 'Public-signal pilot before CRM cleanup',
      opportunity_owner: 'BD / RevOps',
      strategic_tags: 'revenue; strategic',
    },
    0
  ),
  withFactorColumns(
    {
      opportunity_id: 'BV-1',
      opportunity_name: 'Customer Support Case Agent',
      theme: 'Customer Support',
      Rank: '1',
      Gain_Index: '88',
      Pain_Index: '40',
      Priority_Score: '79.6',
      status: 'active',
      source: 'Week 2 readout',
      wave: 'Wave 1',
      decision_label: 'Do now',
      quadrant: 'Do Now / Quick Wins',
      confidence_score: '3',
      type_internal_external: 'Internal',
      impact_type: 'Margin',
      problem_statement: 'Support context is lost across case handoffs',
      solution_summary: 'Classify case type retrieve trusted KB and draft replies',
      potential_impact: 'Lower cost-to-serve and faster response times',
      feasibility_notes: 'Requires trusted KB and support queue access',
      evidence_summary: 'Strong workflow volume and existing support data',
      evidence_caveat: 'Data ownership and KB quality need validation',
      source_systems: 'Service Cloud; KB; Snowflake',
      data_fields_needed: 'case type; status; priority; timestamps; owner; CSAT',
      pilot_wedge: 'Start with top case types and human-approved replies',
      opportunity_owner: 'Support leadership',
      strategic_tags: 'cost_curve; retention',
    },
    1
  ),
];

export const CSV_IMPORT_TEMPLATE = [
  Array.from(IMPORT_COLUMNS),
  ...TEMPLATE_ROWS.map((row) => IMPORT_COLUMNS.map((column) => row[column] ?? '')),
]
  .map((row) => row.map(csvCell).join(','))
  .join('\n')
  .concat('\n');

export const JSON_IMPORT_TEMPLATE = JSON.stringify(
  {
    company_name: 'Example PortCo',
    assessment_week: 'Week 2',
    portfolio_snapshot_date: '2026-05-22',
    gain_factors: [
      ...GAIN_FACTORS.map(({ label, weight }) => ({ label, weight })),
    ],
    pain_factors: [
      ...PAIN_FACTORS.map(({ label, weight }) => ({ label, weight })),
    ],
    opportunities: [
      {
        opportunity_id: 'RN-1',
        opportunity_name: 'Agentic Prospecting + BD Intelligence',
        theme: 'Business Development',
        Rank: 1,
        Gain_Index: 86,
        Pain_Index: 41,
        Priority_Score: 77.5,
        status: 'active',
        source: 'Eliza stakeholder interviews',
        wave: 'Wave 1',
        decision_label: 'Strategic bet',
        quadrant: 'Strategic Bets',
        confidence_score: 3,
        problem_statement: 'No systematic targeting engine.',
        solution_summary: 'Use public signals before CRM data is fixed.',
        potential_impact: 'Revenue expansion and stronger account targeting.',
        feasibility_notes: 'Salesforce quality remains a caveat.',
        evidence_summary: 'COO and BD confirmed fragmented lists.',
        evidence_caveat: 'Project Orbit dependency.',
        source_systems: 'Salesforce; Project Orbit',
        data_fields_needed: 'account lists; public signals; CRM owner',
        pilot_wedge: 'Public-signal pilot before CRM cleanup',
        opportunity_owner: 'BD / RevOps',
        strategic_tags: 'revenue; strategic',
        gain: GAIN_FACTORS.map((factor) => factor.examples[0]),
        pain: PAIN_FACTORS.map((factor) => factor.examples[0]),
      },
    ],
  },
  null,
  2
);

function withFactorColumns(base: Record<string, string>, exampleIndex: 0 | 1) {
  const row: Record<string, string> = { ...base };
  GAIN_FACTORS.forEach((factor, index) => {
    row[`gain_factor_${index + 1}_label`] = factor.label;
    row[`gain_factor_${index + 1}_weight`] = `${Math.round(factor.weight * 100)}%`;
    row[`gain_${index + 1}`] = String(factor.examples[exampleIndex]);
  });
  PAIN_FACTORS.forEach((factor, index) => {
    row[`pain_factor_${index + 1}_label`] = factor.label;
    row[`pain_factor_${index + 1}_weight`] = `${Math.round(factor.weight * 100)}%`;
    row[`pain_${index + 1}`] = String(factor.examples[exampleIndex]);
  });
  return row;
}

function csvCell(value: string) {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function downloadImportTemplate(format: ImportTemplateFormat) {
  if (format === 'xlsx') {
    downloadXlsxTemplate();
    return;
  }
  const content = format === 'csv' ? CSV_IMPORT_TEMPLATE : JSON_IMPORT_TEMPLATE;
  const type = format === 'csv' ? 'text/csv' : 'application/json';
  const extension = format === 'csv' ? 'csv' : 'json';
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `opportunity-ranker-import-template.${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadXlsxTemplate() {
  const workbook = XLSX.utils.book_new();
  const opportunities = XLSX.utils.aoa_to_sheet([
    Array.from(IMPORT_COLUMNS),
    ...TEMPLATE_ROWS.map((row) => IMPORT_COLUMNS.map((column) => row[column] ?? '')),
  ]);
  const scoringFactors = XLSX.utils.aoa_to_sheet([
    ['factor_type', 'label', 'weight'],
    ...GAIN_FACTORS.map((factor) => [factor.kind, factor.label, factor.weight]),
    ...PAIN_FACTORS.map((factor) => [factor.kind, factor.label, factor.weight]),
  ]);
  const instructions = XLSX.utils.aoa_to_sheet([
    ['Opportunity Ranker Upload Template'],
    ['Use the Opportunities sheet as the upload source. Keep one opportunity per row.'],
    ['Rank, Gain_Index, Pain_Index, and Priority_Score are source-of-truth when provided.'],
    ['If Priority_Score is blank but Gain_Index/Pain_Index are provided, Forge derives priority from those indexes.'],
    ['Factor scores must be 1 to 5 and are used only when source scorecard columns are blank or edited later.'],
    ['Use Scoring Factors to define company-specific gain/pain labels and weights.'],
    ['Save as .xlsx or export the Opportunities sheet as .csv before uploading.'],
  ]);
  const dictionary = XLSX.utils.aoa_to_sheet([
    ['column', 'description'],
    ['opportunity_id', 'Stable id used across weekly revisions, such as RN-1 or BV-1.'],
    ['opportunity_name', 'Client-facing opportunity name.'],
    ['theme', 'Business area, workflow, or strategic theme.'],
    ['Rank', 'Authoritative rank when provided. Ranked rows sort before unranked rows.'],
    ['Gain_Index', 'Uploaded scorecard gain index on a 0-100 scale.'],
    ['Pain_Index', 'Uploaded scorecard pain index on a 0-100 scale.'],
    ['Priority_Score', 'Uploaded final priority score. Leave blank to derive from gain/pain indexes or factor scores.'],
    ['status', 'active, deferred, inflight, or shipped.'],
    ['gain_1..gain_5', 'Factor-level value scores from 1 to 5.'],
    ['pain_1..pain_8', 'Factor-level feasibility/pain scores from 1 to 5.'],
    ['strategic_tags', 'Semicolon-separated tags such as revenue; retention; cost_curve.'],
  ]);
  XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, opportunities, 'Opportunities');
  XLSX.utils.book_append_sheet(workbook, scoringFactors, 'Scoring Factors');
  XLSX.utils.book_append_sheet(workbook, dictionary, 'Data Dictionary');
  XLSX.writeFile(workbook, 'opportunity-ranker-upload-template.xlsx');
}
